import { useEffect, useRef } from "react";
import DOMPurify from "dompurify";
import { Bold, Italic, List, ListOrdered, RemoveFormatting } from "lucide-react";

const ALLOWED = { ALLOWED_TAGS: ["b", "strong", "i", "em", "u", "ul", "ol", "li", "p", "br"], ALLOWED_ATTR: [] };
export const cleanHtml = (html) => DOMPurify.sanitize(html || "", ALLOWED);
export const htmlToText = (html) => { const d = document.createElement("div"); d.innerHTML = cleanHtml(html); return (d.textContent || "").trim(); };
export const looksLikeHtml = (s) => /<\/?(b|strong|i|em|u|ul|ol|li|p|br)\b/i.test(s || "");

export function RichText({ html, className = "", testId }) {
  if (!html) return null;
  const safe = looksLikeHtml(html) ? cleanHtml(html) : cleanHtml(html.replace(/\n/g, "<br>"));
  return <div data-testid={testId} className={`rich-text ${className}`} dangerouslySetInnerHTML={{ __html: safe }} />;
}

export function RichTextEditor({ value, onChange, placeholder, className = "", testId = "rich-editor", minRows = 4 }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const incoming = looksLikeHtml(value) ? cleanHtml(value) : cleanHtml((value || "").replace(/\n/g, "<br>"));
    if (el.innerHTML !== incoming && document.activeElement !== el) el.innerHTML = incoming;
  }, [value]);

  const emit = () => onChange(cleanHtml(ref.current.innerHTML).replace(/^(<br>)+|(<br>)+$/g, ""));
  const cmd = (c) => (e) => { e.preventDefault(); ref.current.focus(); document.execCommand(c, false, null); emit(); };

  const Btn = ({ icon: Icon, c, label }) => (
    <button type="button" onMouseDown={cmd(c)} title={label} aria-label={label} data-testid={`${testId}-${c}`}
      className="rounded-lg p-1.5 text-sbg-burgundy/70 hover:bg-sbg-blush hover:text-sbg-burgundy">
      <Icon className="h-4 w-4" />
    </button>
  );

  return (
    <div className={`rounded-xl border border-sbg-raspberry/20 bg-white focus-within:border-sbg-fuchsia ${className}`}>
      <div className="flex items-center gap-0.5 border-b border-sbg-raspberry/10 px-2 py-1">
        <Btn icon={Bold} c="bold" label="Bold" />
        <Btn icon={Italic} c="italic" label="Italic" />
        <Btn icon={List} c="insertUnorderedList" label="Bullet list" />
        <Btn icon={ListOrdered} c="insertOrderedList" label="Numbered list" />
        <Btn icon={RemoveFormatting} c="removeFormat" label="Clear formatting" />
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        data-testid={testId}
        data-placeholder={placeholder}
        onInput={emit}
        onBlur={emit}
        onPaste={(e) => { e.preventDefault(); document.execCommand("insertText", false, e.clipboardData.getData("text/plain")); }}
        className="rich-text rich-editor px-3 py-2 text-base text-sbg-burgundy outline-none"
        style={{ minHeight: `${minRows * 1.6}rem` }}
      />
    </div>
  );
}
